import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { collectionPackagesApi } from "@/api/collectionPackages";
import { extractErrorMessage } from "@/api/client";
import {
  assembleCompensationSchedule,
  defaultCompensationValues,
  makeOpenTier,
  serializeCompensation,
  type CompensationTier,
  type CompensationValues,
} from "@/components/Collection/vendorCompensation";

// WS-B — bespoke compensation editor for the Vendor Engagement &
// Compensation Schedule letter. Staff choose a Tiered table (variable rows;
// the last row is the open-ended top tier) OR a Custom free-text
// arrangement. On every change the editor reports the serialized JSON
// (structured values + assembled `{{CompensationSchedule}}` block) upward;
// the composer merges it with the Section-1 sender values at save.
//
// The "Preview schedule" button mirrors SenderDetailsSection's
// button-triggered, single-flight, object-URL iframe pattern, but MERGES the
// Section-1 sender values so staff see the COMPLETE letter PDF.

interface Props {
  templateId: string;
  templateName: string;
  /** The Section-1 sender values JSON for this template (from
   *  SenderDetailsSection), merged into the preview so the rendered PDF is
   *  the full letter, not just the schedule block. */
  senderValuesJson: string | undefined;
  onValuesChange: (templateId: string, valuesJson: string) => void;
}

function asNumberOrNull(raw: string): number | null {
  return raw.trim() === "" ? null : Number(raw);
}

export function VendorCompensationEditor({
  templateId,
  templateName,
  senderValuesJson,
  onValuesChange,
}: Props) {
  const [values, setValues] = useState<CompensationValues>(() =>
    defaultCompensationValues(),
  );

  // Report the serialized values upward on every change.
  const serialized = useMemo(() => serializeCompensation(values), [values]);
  useEffect(() => {
    onValuesChange(templateId, serialized);
  }, [templateId, serialized, onValuesChange]);

  const setMethod = (method: CompensationValues["compensationMethod"]) =>
    setValues((prev) =>
      prev.compensationMethod === method ? prev : { ...prev, compensationMethod: method },
    );

  const updateTier = (index: number, patch: Partial<CompensationTier>) =>
    setValues((prev) => ({
      ...prev,
      tiers: prev.tiers.map((t, i) => (i === index ? { ...t, ...patch } : t)),
    }));

  const addTier = () =>
    // The new row becomes the open-ended top tier; the previously-last row is
    // no longer top, so the UI now renders its "Up to $___" threshold input.
    setValues((prev) => ({ ...prev, tiers: [...prev.tiers, makeOpenTier()] }));

  const removeTier = (index: number) =>
    setValues((prev) => {
      if (prev.tiers.length <= 1) return prev; // enforce ≥1 row
      const next = prev.tiers.filter((_, i) => i !== index);
      // The remaining last row is always the open-ended top tier.
      const lastIdx = next.length - 1;
      next[lastIdx] = { ...next[lastIdx], threshold: null };
      return { ...prev, tiers: next };
    });

  // Server-rendered PDF preview of the COMPLETE letter. Single-flight,
  // button-triggered (LibreOffice cold-starts serialize poorly) — mirrors
  // SenderDetailsSection.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewStale, setPreviewStale] = useState(false);
  const urlRef = useRef<string | null>(null);

  // The merged JSON staff would actually save: Section-1 sender values +
  // compensation values + the assembled schedule block. Keys are disjoint.
  const mergedJson = useMemo(() => {
    let sender: Record<string, unknown> = {};
    if (senderValuesJson) {
      try {
        const parsed = JSON.parse(senderValuesJson) as unknown;
        if (parsed && typeof parsed === "object") sender = parsed as Record<string, unknown>;
      } catch {
        // Ignore malformed sender JSON — preview the compensation block alone.
      }
    }
    return JSON.stringify({
      ...sender,
      compensationMethod: values.compensationMethod,
      tiers: values.tiers,
      customCompensation: values.customCompensation,
      CompensationSchedule: assembleCompensationSchedule(values),
    });
  }, [senderValuesJson, values]);

  // Flag the rendered preview out-of-date when inputs change.
  useEffect(() => {
    if (urlRef.current) setPreviewStale(true);
  }, [mergedJson]);

  // Revoke the object URL on unmount.
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  const runPreview = useCallback(async () => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const blob = await collectionPackagesApi.letterPreview(templateId, mergedJson);
      const url = URL.createObjectURL(blob);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      setPreviewUrl(url);
      setPreviewStale(false);
    } catch (e) {
      setPreviewError(extractErrorMessage(e));
    } finally {
      setPreviewing(false);
    }
  }, [templateId, mergedJson]);

  return (
    <section className="rounded-md border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-3">
      <h3 className="text-small font-semibold text-onyx m-0">
        Compensation schedule — {templateName}
      </h3>
      <p className="text-caption text-gray-mid m-0">
        Choose a tiered table or a custom arrangement. The vendor reviews this
        read-only and signs the letter.
      </p>

      <FormField
        label="Compensation method"
        htmlFor="comp-method"
        helperText="Tiered builds a rate table per Measurement Period; Custom is a free-text arrangement."
      >
        <Select
          id="comp-method"
          value={values.compensationMethod}
          onChange={(e) => setMethod(e.target.value as CompensationValues["compensationMethod"])}
        >
          <option value="tiered">Tiered table</option>
          <option value="custom">Custom arrangement</option>
        </Select>
      </FormField>

      {values.compensationMethod === "tiered" ? (
        <div className="flex flex-col gap-space-3">
          <ul className="divide-y divide-border-light border border-border-light rounded-md m-0 p-0 list-none">
            {values.tiers.map((tier, index) => {
              const isTop = index === values.tiers.length - 1;
              return (
                <li key={index} className="flex flex-col gap-space-3 p-space-3">
                  <div className="flex items-center justify-between gap-space-2">
                    <span className="text-caption font-medium text-gray-mid">
                      {isTop
                        ? `Tier ${index + 1} — top tier (applies to all volume above the previous threshold)`
                        : `Tier ${index + 1}`}
                    </span>
                    <Button
                      type="button"
                      variant="tertiary"
                      size="sm"
                      onClick={() => removeTier(index)}
                      disabled={values.tiers.length <= 1}
                    >
                      <Trash2 size={14} aria-hidden /> Remove
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
                    {!isTop && (
                      <FormField
                        label="Up to ($ per period)"
                        htmlFor={`comp-threshold-${index}`}
                      >
                        <Input
                          id={`comp-threshold-${index}`}
                          type="number"
                          step="0.01"
                          value={tier.threshold == null ? "" : String(tier.threshold)}
                          onChange={(e) =>
                            updateTier(index, { threshold: asNumberOrNull(e.target.value) })
                          }
                          placeholder="e.g. 5000"
                        />
                      </FormField>
                    )}
                    <FormField label="Rate type" htmlFor={`comp-rate-type-${index}`}>
                      <div className="flex items-center gap-space-4 h-10">
                        <label className="flex items-center gap-space-2 text-small text-onyx m-0">
                          <input
                            type="radio"
                            name={`comp-rate-type-${index}`}
                            checked={tier.rateType === "percent"}
                            onChange={() => updateTier(index, { rateType: "percent" })}
                          />
                          Percent
                        </label>
                        <label className="flex items-center gap-space-2 text-small text-onyx m-0">
                          <input
                            type="radio"
                            name={`comp-rate-type-${index}`}
                            checked={tier.rateType === "flat"}
                            onChange={() => updateTier(index, { rateType: "flat" })}
                          />
                          Flat
                        </label>
                      </div>
                    </FormField>
                    {tier.rateType === "percent" ? (
                      <FormField label="Rate (%)" htmlFor={`comp-rate-pct-${index}`}>
                        <Input
                          id={`comp-rate-pct-${index}`}
                          type="number"
                          step="0.01"
                          value={tier.ratePct == null ? "" : String(tier.ratePct)}
                          onChange={(e) =>
                            updateTier(index, { ratePct: asNumberOrNull(e.target.value) })
                          }
                          placeholder="e.g. 7.5"
                        />
                      </FormField>
                    ) : (
                      <FormField label="Rate ($ per period)" htmlFor={`comp-rate-flat-${index}`}>
                        <Input
                          id={`comp-rate-flat-${index}`}
                          type="number"
                          step="0.01"
                          value={tier.rateFlat == null ? "" : String(tier.rateFlat)}
                          onChange={(e) =>
                            updateTier(index, { rateFlat: asNumberOrNull(e.target.value) })
                          }
                          placeholder="e.g. 1500"
                        />
                      </FormField>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          <div>
            <Button type="button" variant="secondary" size="sm" onClick={addTier}>
              <Plus size={14} aria-hidden /> Add tier
            </Button>
          </div>
        </div>
      ) : (
        <FormField
          label="Custom compensation arrangement"
          htmlFor="comp-custom"
          helperText="Free-text terms shown verbatim in the letter."
        >
          <Textarea
            id="comp-custom"
            rows={6}
            value={values.customCompensation}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, customCompensation: e.target.value }))
            }
            placeholder="Describe the compensation arrangement…"
          />
        </FormField>
      )}

      <div className="flex flex-col gap-space-2">
        <div className="flex items-center justify-between gap-space-2">
          <span className="text-caption font-medium text-gray-mid">Preview</span>
          <div className="flex items-center gap-space-3">
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-caption text-gold underline"
              >
                Open / print
              </a>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void runPreview()}
              disabled={previewing}
              loading={previewing}
            >
              {previewUrl ? "Update preview" : "Preview schedule"}
            </Button>
          </div>
        </div>
        {previewStale && previewUrl && !previewing && (
          <p className="text-caption text-gray-mid italic m-0">
            Values changed — click "Update preview" to re-render.
          </p>
        )}
        {previewError ? (
          <p className="text-caption text-status-error m-0" role="alert">
            {previewError}
          </p>
        ) : previewUrl ? (
          <iframe
            src={previewUrl}
            title={`Preview — ${templateName}`}
            className="w-full rounded-md border border-border-light bg-white-warm"
            style={{ height: "640px" }}
          />
        ) : (
          <p className="text-caption text-gray-mid italic m-0">
            {previewing
              ? "Rendering preview…"
              : "Click “Preview schedule” to render the complete letter."}
          </p>
        )}
      </div>
    </section>
  );
}
